import { useEffect, useState, type MouseEvent } from "react";
import NextLink from "next/link";
import Head from "next/head";
import {
  Box,
  Heading,
  IconFa,
  Link,
  Loader,
  Step,
  Text,
  Button,
} from "@cruk/cruk-react-components";
import { useRouter } from "next/compat/router";
import {
  faEnvelope,
  faComment,
  faShareAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebookF,
  faWhatsapp,
  faFacebookMessenger,
  faLinkedin,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { useOptimizelyContext } from "@fwa/src/contexts/OptimizelyContext";
import { useTracking } from "@fwa/src/hooks/useTracking";
import { buildQuery, queryAsString } from "@fwa/src/utils/urlUtils";
import { fwsUrlDonation } from "@fwa/src/services/donationService";
import { fwsUrlPage } from "@fwa/src/services/pageService";
import { fetcher } from "@fwa/src/services/apiClient";
import { firstNameForPage, getBrandForPage } from "@fwa/src/utils/pageUtils";
import { getShareURLsFromEntity } from "@fwa/src/utils/shareUtils";
import {
  isMobile,
  isChrome,
  hasShareApi,
  isBrowser,
} from "@fwa/src/utils/browserUtils";
import { numberWithCommas } from "@fwa/src/utils/formatUtils";
import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { useLayoutEffectBrowser } from "@fwa/src/hooks/useLayoutEffectBrowser";
import { useBrandContext } from "@fwa/src/contexts/BrandContext";

import { FeedItem } from "@fwa/src/components/FeedItem";
import { ErrorPageWrapper } from "@fwa/src/components/ErrorPageWrapper";
import { CopyToClipBoard } from "@fwa/src/components/CopyToClipboard";
import { CogCta } from "@fwa/src/components/CogCta";
import { ErrorBoundary } from "@fwa/src/components/ErrorBoundary";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/DonateConfirmationPage/styles.module.css";

import {
  type FeedItemDonationRealType,
  type AllDonatablePageType,
  type BrandNameType,
  type FundraisingPageType,
} from "@fwa/src/types";

export const DonateConfirmationPage = () => {
  const { trackEventOptimizely } = useOptimizelyContext();
  const [donationState, setDonationState] =
    useState<FeedItemDonationRealType>();
  const router = useRouter();
  const {
    slug,
    seller_reference: sellerReference,
    transaction_id: transactionId,
    signature,
  } = router?.query || {};
  const slugString = queryAsString(slug);
  const url = slugString.length
    ? `${fwsUrlPage({ pageId: slugString })}`
    : null;
  const { data: page, error } = useSwrGet<AllDonatablePageType>(url);
  const entityType = donationState?.entityType || "DonationReal";
  const themeName = page
    ? (page?.theme as BrandNameType) || getBrandForPage(page)
    : null;

  const [showMobile, setShowMobile] = useState(true);
  const [showShareApi, setShowShareApi] = useState(true);
  const [brandName, setBrandName] = useBrandContext();
  const { trackError, trackEventGtm } = useTracking();
  const shareClick = (
    e: MouseEvent<HTMLElement>,
    key: string,
    shareUrl: string,
    isOwner?: boolean,
  ) => {
    // The mobile chrome browser does not allow js to open apps so
    // we are removing the event handler in this case.
    if ((isChrome && isMobile) || key === "email") {
      // A hail mary in case prevent default on a link wont work with certian mobile user agents
      trackEventGtm({
        event: "share",
        action: key,
        entityType,
        isOwner,
      });
      trackEventOptimizely({
        eventKey: "of_donate_confirmation_share",
      });
      return;
    }
    trackEventGtm({
      event: "share",
      action: key,
      entityType,
      isOwner,
    });
    trackEventOptimizely({
      eventKey: "of_donate_confirmation_share",
    });
    e.preventDefault();
    openShare(shareUrl, key, isOwner || false);
  };

  const openShare = (shareUrl: string, shareType: string, isOwner: boolean) => {
    if (!isBrowser) {
      return;
    }
    trackEventGtm({
      event: "share",
      action: shareType,
      entityType,
      isOwner,
    });
    return window.open(
      shareUrl,
      `${shareType}${Math.random()}}`,
      "toolbar=0,status=0,width=548,height=400",
    );
  };

  const handleMobileShareClick = () => {
    if (!isBrowser || !url) return;
    trackEventOptimizely({
      eventKey: "of_donate_confirmation_share",
    });
    trackEventGtm({
      event: "share_cta",
      entityType,
      // cta: ctaLocation,
      // isOwner,
    });
    window.navigator
      .share({
        title: "Share this page",
        url: getShareURLsFromEntity(page!).copy,
      })
      .catch((err: Error) => {
        trackError(err, { component: "Share" });
      });
  };

  // this grabs urls from browser so we need this to happen when window exists
  useLayoutEffectBrowser(() => {
    if (showMobile !== isMobile) {
      setShowMobile(isMobile);
    }
    if (showShareApi !== hasShareApi) {
      setShowShareApi(hasShareApi);
    }
  }, []);

  useEffect(() => {
    if (!!themeName && themeName !== brandName) {
      setBrandName(themeName);
    }
  }, [themeName, brandName, setBrandName]);

  useEffect(() => {
    // router can be ready but it still can't read query strings
    // best wait util it can confrim they exist before attempting to use them
    if (Object.keys(router?.query || "").length < 1) return;
    fetcher(
      `${fwsUrlDonation({
        donationId: sellerReference as string,
      })}${buildQuery({
        transaction_id: queryAsString(transactionId),
        signature: queryAsString(signature),
      })}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          result: 0,
        }),
      },
    )
      .then((res) => res as FeedItemDonationRealType)
      .then((donation: FeedItemDonationRealType) => {
        setDonationState(donation);
        return null;
      })
      .catch((err: Error) => {
        trackError(err, { component: "DonateConfirmationPage" });
        trackError(new Error("fwsUrlDonation patch failed"), {
          component: "DonateConfirmationPage",
          transactionId,
          signature,
          sellerReference,
        });
        // await redirectToFailPage(transactionId as string);
      });
  }, [router?.isReady]);

  const recipientName = page
    ? page.entityType === "SuperTeam"
      ? page.title
      : firstNameForPage(page)
    : "";

  const hasRestriction: boolean =
    !!page?.fundraisingPageType?.restriction ||
    !!page?.fundraisingPageType?.brand?.restriction ||
    !!(page as FundraisingPageType)?.restriction ||
    !!(page as FundraisingPageType)?.brand?.restriction;

  return (
    <ErrorPageWrapper error={error!}>
      {!!page && (
        <>
          <Head>
            <title>Donate to {page?.title}</title>
          </Head>
          <div className={sharedStyles["content-max-width"]}>
            <Box
              backgroundColor="background-light"
              paddingHorizontal="s"
              paddingVertical="l"
              marginTop="s"
              marginBottom="l"
            >
              <div
                className={sharedStyles["form-wrapper"]}
                data-component="donate-confirm-body"
              >
                <Heading textAlign="center" h1>
                  Donate to {page.title}
                </Heading>

                <Box marginBottom="m">
                  <Step
                    current={4}
                    steps={["Details", "Payment", "Thank you"]}
                  />
                </Box>
                {donationState ? (
                  <>
                    <Heading textAlign="center" h1 textSize="xxl">
                      Thank you {donationState?.donor?.forename} for donating £
                      {numberWithCommas(donationState.amount.toFixed(2))}
                    </Heading>
                    <Text>
                      Your donation is giving more people more moments with the
                      people they love.
                    </Text>
                    <Box
                      marginVertical="s"
                      backgroundColor="background-mid"
                      padding="s"
                    >
                      <Text>
                        {`Help ${recipientName} raise more towards life-saving work
                        by sharing their page.`}
                      </Text>
                      {showShareApi && showMobile ? (
                        <Button
                          type="button"
                          appearance="primary"
                          onClick={handleMobileShareClick}
                          full
                          data-cta-type="share-page"
                        >
                          <IconFa faIcon={faShareAlt} />
                          <span>Share the page</span>
                        </Button>
                      ) : (
                        <div className={sharedStyles["column"]}>
                          <Box
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              flexWrap: "wrap",
                              justifyContent: "center",
                            }}
                            marginBottom="s"
                            data-component="donate-confirm-share"
                          >
                            <a
                              className={styles["donation-confirm-social-link"]}
                              style={{ backgroundColor: "#4267b2" }}
                              href={getShareURLsFromEntity(page).facebook}
                              aria-label="share on facebook"
                              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                                shareClick(
                                  e,
                                  "facebook",
                                  getShareURLsFromEntity(page).facebook,
                                );
                              }}
                              data-cta-type="share-facebook"
                            >
                              <IconFa
                                faIcon={faFacebookF}
                                color="text-light"
                                size="2rem"
                              />
                            </a>

                            <a
                              className={styles["donation-confirm-social-link"]}
                              style={{ backgroundColor: "#000000" }}
                              href={getShareURLsFromEntity(page).twitter}
                              aria-label="share on twitter"
                              onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                                shareClick(
                                  e,
                                  "twitter",
                                  getShareURLsFromEntity(page).twitter,
                                )
                              }
                              data-cta-type="share-twitter"
                            >
                              <IconFa
                                faIcon={faXTwitter}
                                color="text-light"
                                size="2rem"
                              />
                            </a>

                            {showMobile ? (
                              <>
                                <a
                                  className={
                                    styles["donation-confirm-social-link"]
                                  }
                                  style={{ backgroundColor: "#25D366" }}
                                  href={getShareURLsFromEntity(page).whatsapp}
                                  aria-label="share on whatsapp"
                                  onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                                    shareClick(
                                      e,
                                      "whatsapp",
                                      getShareURLsFromEntity(page).whatsapp,
                                    )
                                  }
                                  data-cta-type="share-whatsapp"
                                >
                                  <IconFa
                                    faIcon={faWhatsapp}
                                    color="text-light"
                                    size="2rem"
                                  />
                                </a>
                                <a
                                  className={
                                    styles["donation-confirm-social-link"]
                                  }
                                  style={{ backgroundColor: "#0084FF" }}
                                  href={
                                    getShareURLsFromEntity(page).fbmessenger
                                  }
                                  aria-label="share on facebook messenger"
                                  onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                                    shareClick(
                                      e,
                                      "fbmessenger",
                                      getShareURLsFromEntity(page).fbmessenger,
                                    )
                                  }
                                  data-cta-type="share-facebook-messenger"
                                >
                                  <IconFa
                                    faIcon={faFacebookMessenger}
                                    color="text-light"
                                    size="2rem"
                                  />
                                </a>
                                <a
                                  className={
                                    styles["donation-confirm-social-link"]
                                  }
                                  style={{ backgroundColor: "#53d769" }}
                                  href={getShareURLsFromEntity(page).sms}
                                  aria-label="share by sms"
                                  onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                                    shareClick(
                                      e,
                                      "sms",
                                      getShareURLsFromEntity(page).sms,
                                    )
                                  }
                                  data-cta-type="share-sms"
                                >
                                  <IconFa
                                    faIcon={faComment}
                                    color="text-light"
                                    size="2rem"
                                  />
                                </a>
                              </>
                            ) : null}

                            <a
                              className={styles["donation-confirm-social-link"]}
                              style={{ backgroundColor: "#0077b5" }}
                              href={getShareURLsFromEntity(page).linkedIn}
                              aria-label="share on linkedin"
                              onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                                shareClick(
                                  e,
                                  "linkedin",
                                  getShareURLsFromEntity(page).linkedIn,
                                )
                              }
                              data-cta-type="share-linkedin"
                            >
                              <IconFa
                                faIcon={faLinkedin}
                                color="text-light"
                                size="2rem"
                              />
                            </a>

                            <a
                              className={styles["donation-confirm-social-link"]}
                              style={{
                                backgroundColor: "var(--clr-primary, #005ea5)",
                              }}
                              href={getShareURLsFromEntity(page).email}
                              aria-label="share with email"
                              onClick={(e: MouseEvent<HTMLAnchorElement>) =>
                                shareClick(
                                  e,
                                  "email",
                                  getShareURLsFromEntity(page).email,
                                )
                              }
                              data-cta-type="share-email"
                            >
                              <IconFa
                                faIcon={faEnvelope}
                                color="text-light"
                                size="2rem"
                              />
                            </a>
                          </Box>

                          <CopyToClipBoard
                            shareCopy={getShareURLsFromEntity(page).copy}
                            onClick={() => {
                              trackEventGtm({
                                event: "share",
                                action: "copy url",
                              });
                              trackEventOptimizely({
                                eventKey: "of_donate_confirmation_share",
                              });
                            }}
                          />
                        </div>
                      )}
                    </Box>
                    <Box marginTop="m">
                      {hasRestriction ? null : <CogCta />}
                    </Box>

                    <Box>
                      <NextLink
                        href={`${page.url.replace(
                          /https?:\/\/[^/]*/,
                          "",
                        )}?feed=${donationState.uniqueId}`}
                        onClick={() =>
                          trackEventOptimizely({
                            eventKey: "of_donate_confirmation_return_to_page",
                          })
                        }
                        data-cta-type="link-donation-on-page"
                      >
                        <Link appearance="primary" as="span">
                          See the donation on the page
                        </Link>
                      </NextLink>
                    </Box>

                    <Box>
                      <ErrorBoundary>
                        <FeedItem
                          isDonationConfirmation
                          feedItem={donationState}
                          handleDelete={() => {
                            // placeholder no op
                          }}
                          handleEdit={(editedDonation) =>
                            setDonationState(
                              editedDonation as FeedItemDonationRealType,
                            )
                          }
                          pageUrl={page.url}
                          pageId={page.uniqueId}
                          isStravaMetric={false}
                          fitbitDailyTargetSteps={0}
                        />
                      </ErrorBoundary>
                    </Box>

                    <Text>
                      Your reference number is:{" "}
                      <Text as="span" textWeight="heavy">
                        {transactionId}
                      </Text>
                      {showMobile
                        ? null
                        : ". We've sent you an email so you have a record of this."}
                    </Text>
                    <Text>
                      Need help? Please email our{" "}
                      <Link
                        href="mailto:supporter.services@cancer.org.uk"
                        data-cta-type="link-mail-to-supporter-services"
                      >
                        Supporter Services team
                      </Link>{" "}
                      or call{" "}
                      <Link
                        href="tel:+443001231022"
                        data-cta-type="link-tel-support"
                      >
                        0300 123 1022
                      </Link>{" "}
                      (lines are open 8am-6pm Monday to Friday).
                    </Text>
                  </>
                ) : (
                  <Loader />
                )}
              </div>
            </Box>
          </div>
        </>
      )}
    </ErrorPageWrapper>
  );
};

export default DonateConfirmationPage;
